import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getOpportunityMeta, previewOpportunity, createOpportunity, formatApiError } from "../lib/api";
import { OpportunityIntelligence } from "../components/OpportunityIntelligence";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const Field = ({ label, children, hint }) => (
  <label className="flex flex-col gap-1">
    <span className="text-xs font-medium text-slate-500">{label}</span>
    {children}
    {hint && <span className="text-[11px] text-slate-400">{hint}</span>}
  </label>
);
const inputCls = "h-10 rounded-md border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-atlas-blue/40";

const emptyCost = () => ({ category: "transport", amount: "", currency: "", basis: "ESTIMATED", note: "" });
const emptyComp = () => ({ price: "", currency: "", year: "", mileage: "", note: "" });

// Static fallback so the corridor selects render (and honour prefilled DE/NL params)
// immediately, before the authoritative /opportunities/meta list resolves.
const FALLBACK_COUNTRIES = [
  { code: "DE", label: "Germany", currency: "EUR" }, { code: "NL", label: "Netherlands", currency: "EUR" },
  { code: "FR", label: "France", currency: "EUR" }, { code: "IT", label: "Italy", currency: "EUR" },
  { code: "ES", label: "Spain", currency: "EUR" }, { code: "BE", label: "Belgium", currency: "EUR" },
  { code: "AT", label: "Austria", currency: "EUR" }, { code: "PL", label: "Poland", currency: "PLN" },
  { code: "SE", label: "Sweden", currency: "SEK" }, { code: "CZ", label: "Czech Republic", currency: "CZK" },
];

export default function OpportunityWorkspace() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [meta, setMeta] = useState(null);
  const [v, setV] = useState({ make: params.get("make") || "", model: params.get("model") || "", version: "", year: "", mileage: "", fuel: "", transmission: "", classification: params.get("classification") || "Standard", source_listing_url: "" });
  const [source, setSource] = useState(params.get("source") || "DE");
  const [dest, setDest] = useState(params.get("dest") || "NL");
  const [asking, setAsking] = useState(params.get("asking") || "");
  const [askingCcy, setAskingCcy] = useState("");
  const [costs, setCosts] = useState([emptyCost()]);
  const [comps, setComps] = useState([emptyComp()]);
  const [destEst, setDestEst] = useState("");
  const [targetMargin, setTargetMargin] = useState("");
  const [riskFlags, setRiskFlags] = useState([]);
  const [notes, setNotes] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showPrefill, setShowPrefill] = useState(
    ["make", "model", "source", "dest", "asking", "classification"].some((k) => params.get(k))
  );

  useEffect(() => { getOpportunityMeta().then(setMeta).catch(() => {}); }, []);

  if (user === null) return <><div className="p-10 text-center text-slate-400">Checking access…</div></>;
  if (!user || user.role !== "admin") {
    return <><div className="max-w-xl mx-auto py-24 text-center">
      <h1 className="text-2xl font-bold text-atlas-ink">Operator access required</h1>
      <p className="text-slate-500 mt-2">The analysis workspace is available to Atlas operators. Sign in with an operator account.</p>
      <Button className="mt-5 bg-atlas-blue text-white" onClick={() => navigate("/login")} data-testid="workspace-login-btn">Sign in</Button>
    </div></>;
  }

  const countries = meta?.countries || FALLBACK_COUNTRIES;
  const currencyFor = (iso) => (countries.find((c) => c.code === iso)?.currency) || "EUR";
  const payload = () => ({
    vehicle: { ...v, year: v.year ? Number(v.year) : null, mileage: v.mileage ? Number(v.mileage) : null },
    source_country: source, destination_country: dest,
    asking_price: Number(asking || 0), asking_currency: askingCcy || currencyFor(source),
    costs: costs.filter((c) => c.amount !== "").map((c) => ({ ...c, amount: Number(c.amount), currency: c.currency || currencyFor(dest) })),
    comparables: comps.filter((c) => c.price !== "").map((c) => ({ price: Number(c.price), currency: c.currency || currencyFor(dest), year: c.year ? Number(c.year) : null, mileage: c.mileage ? Number(c.mileage) : null, note: c.note })),
    destination_value_estimate: destEst ? Number(destEst) : null,
    destination_value_basis: destEst ? "ESTIMATED" : null,
    target_margin_pct: targetMargin ? Number(targetMargin) / 100 : null,
    risk_flags: riskFlags, notes,
  });

  const run = async (save) => {
    if (!v.make || !v.model || !asking) { toast.error("Enter at least make, model and asking price."); return; }
    setBusy(true);
    try {
      const fn = save ? createOpportunity : previewOpportunity;
      const data = await fn(payload());
      const opp = save ? data.opportunity : data;
      setResult(opp);
      if (save) { toast.success("Opportunity saved."); navigate(`/intelligence/${opp.id}`); }
      else toast.success("Analysis calculated.");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Calculation failed.");
    } finally { setBusy(false); }
  };

  const setCost = (idx, k, val) => setCosts((cs) => cs.map((c, i) => (i === idx ? { ...c, [k]: val } : c)));
  const setComp = (idx, k, val) => setComps((cs) => cs.map((c, i) => (i === idx ? { ...c, [k]: val } : c)));
  const toggleRisk = (key) => setRiskFlags((f) => (f.includes(key) ? f.filter((x) => x !== key) : [...f, key]));

  return (
    <>
      <div className="max-w-[1400px] mx-auto px-5 md:px-8 py-8">
        <div className="mb-6">
          <span className="text-[11px] uppercase tracking-widest text-slate-400 font-mono">Atlas Intelligence · manual analysis</span>
          <h1 className="text-3xl font-bold text-atlas-ink mt-1">New acquisition opportunity</h1>
          <p className="text-slate-500 text-sm mt-1">Enter a vehicle and a source → destination corridor. Atlas computes landed cost, destination value, margin, the maximum price to pay, and a BUY / NEGOTIATE / PASS decision.</p>
        </div>

        {showPrefill && (
          <div className="mb-6 flex items-start gap-3 rounded-lg border border-atlas-blue/30 bg-atlas-blue/5 px-4 py-3" data-testid="workspace-prefill-banner">
            <span className="mt-0.5 text-atlas-blue">✓</span>
            <p className="text-sm text-slate-600 flex-1">
              Prefilled from your homepage search. Review and adjust the vehicle, corridor and price before calculating.
            </p>
            <button onClick={() => setShowPrefill(false)} className="text-slate-400 hover:text-slate-600 text-lg leading-none" data-testid="workspace-prefill-dismiss" aria-label="Dismiss">×</button>
          </div>
        )}

        <div className="grid lg:grid-cols-2 gap-8">
          {/* FORM */}
          <div className="space-y-6" data-testid="workspace-form">
            <section className="rounded-lg border border-slate-200 bg-white p-5">
              <h3 className="text-sm font-semibold text-atlas-ink mb-3">1 · Vehicle</h3>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Make"><input className={inputCls} value={v.make} onChange={(e) => setV({ ...v, make: e.target.value })} data-testid="wf-make" /></Field>
                <Field label="Model"><input className={inputCls} value={v.model} onChange={(e) => setV({ ...v, model: e.target.value })} data-testid="wf-model" /></Field>
                <Field label="Version / trim"><input className={inputCls} value={v.version} onChange={(e) => setV({ ...v, version: e.target.value })} /></Field>
                <Field label="Year"><input className={inputCls} type="number" value={v.year} onChange={(e) => setV({ ...v, year: e.target.value })} data-testid="wf-year" /></Field>
                <Field label="Mileage (km)"><input className={inputCls} type="number" value={v.mileage} onChange={(e) => setV({ ...v, mileage: e.target.value })} data-testid="wf-mileage" /></Field>
                <Field label="Fuel"><input className={inputCls} value={v.fuel} onChange={(e) => setV({ ...v, fuel: e.target.value })} /></Field>
                <Field label="Transmission"><input className={inputCls} value={v.transmission} onChange={(e) => setV({ ...v, transmission: e.target.value })} /></Field>
                <Field label="Classification">
                  <select className={inputCls} value={v.classification} onChange={(e) => setV({ ...v, classification: e.target.value })} data-testid="wf-classification">
                    {(meta?.classifications || ["Standard"]).map((c) => <option key={c}>{c}</option>)}
                  </select>
                </Field>
                <div className="col-span-2"><Field label="Source listing URL (optional)"><input className={inputCls} value={v.source_listing_url} onChange={(e) => setV({ ...v, source_listing_url: e.target.value })} /></Field></div>
              </div>
            </section>

            <section className="rounded-lg border border-slate-200 bg-white p-5">
              <h3 className="text-sm font-semibold text-atlas-ink mb-3">2 · Corridor & price</h3>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Source country" hint={`Currency: ${currencyFor(source)}`}>
                  <select className={inputCls} value={source} onChange={(e) => setSource(e.target.value)} data-testid="wf-source-country">
                    {countries.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
                  </select>
                </Field>
                <Field label="Destination country" hint={`Currency: ${currencyFor(dest)}`}>
                  <select className={inputCls} value={dest} onChange={(e) => setDest(e.target.value)} data-testid="wf-dest-country">
                    {countries.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
                  </select>
                </Field>
                <Field label="Asking price"><input className={inputCls} type="number" value={asking} onChange={(e) => setAsking(e.target.value)} data-testid="wf-asking" /></Field>
                <Field label="Asking currency" hint="Defaults to source currency">
                  <input className={inputCls} value={askingCcy} placeholder={currencyFor(source)} onChange={(e) => setAskingCcy(e.target.value.toUpperCase())} />
                </Field>
              </div>
            </section>

            <section className="rounded-lg border border-slate-200 bg-white p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-atlas-ink">3 · Acquisition costs</h3>
                <button className="text-xs text-atlas-blue" onClick={() => setCosts([...costs, emptyCost()])} data-testid="wf-add-cost">+ Add cost</button>
              </div>
              <div className="space-y-2">
                {costs.map((c, idx) => (
                  <div key={idx} className="grid grid-cols-[1.3fr_1fr_1.1fr_auto] gap-2 items-center">
                    <select className={inputCls} value={c.category} onChange={(e) => setCost(idx, "category", e.target.value)} data-testid={`wf-cost-cat-${idx}`}>
                      {(meta?.costCategories || []).map((cc) => <option key={cc.key} value={cc.key}>{cc.label}</option>)}
                    </select>
                    <input className={inputCls} type="number" placeholder="Amount" value={c.amount} onChange={(e) => setCost(idx, "amount", e.target.value)} data-testid={`wf-cost-amt-${idx}`} />
                    <select className={inputCls} value={c.basis} onChange={(e) => setCost(idx, "basis", e.target.value)}>
                      <option value="ESTIMATED">Estimated</option>
                      <option value="HUMAN_VERIFIED">Verified</option>
                    </select>
                    <button className="text-slate-400 hover:text-rose-600 text-lg px-1" onClick={() => setCosts(costs.filter((_, i) => i !== idx))}>×</button>
                  </div>
                ))}
              </div>
            </section>

            <section className="rounded-lg border border-slate-200 bg-white p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-atlas-ink">4 · Destination market (comparables)</h3>
                <button className="text-xs text-atlas-blue" onClick={() => setComps([...comps, emptyComp()])} data-testid="wf-add-comp">+ Add comparable</button>
              </div>
              <div className="space-y-2">
                {comps.map((c, idx) => (
                  <div key={idx} className="grid grid-cols-[1.2fr_0.8fr_1fr_1.4fr_auto] gap-2 items-center">
                    <input className={inputCls} type="number" placeholder="Price" value={c.price} onChange={(e) => setComp(idx, "price", e.target.value)} data-testid={`wf-comp-price-${idx}`} />
                    <input className={inputCls} type="number" placeholder="Year" value={c.year} onChange={(e) => setComp(idx, "year", e.target.value)} />
                    <input className={inputCls} type="number" placeholder="km" value={c.mileage} onChange={(e) => setComp(idx, "mileage", e.target.value)} />
                    <input className={inputCls} placeholder="note / source" value={c.note} onChange={(e) => setComp(idx, "note", e.target.value)} />
                    <button className="text-slate-400 hover:text-rose-600 text-lg px-1" onClick={() => setComps(comps.filter((_, i) => i !== idx))}>×</button>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <Field label={`Or destination value estimate (${currencyFor(dest)})`} hint="Overrides comparable median">
                  <input className={inputCls} type="number" value={destEst} onChange={(e) => setDestEst(e.target.value)} data-testid="wf-dest-estimate" />
                </Field>
                <Field label="Target margin %" hint={`Default ${((meta?.defaultTargetMargin || 0.15) * 100).toFixed(0)}%`}>
                  <input className={inputCls} type="number" value={targetMargin} placeholder={((meta?.defaultTargetMargin || 0.15) * 100).toFixed(0)} onChange={(e) => setTargetMargin(e.target.value)} data-testid="wf-target-margin" />
                </Field>
              </div>
            </section>

            <section className="rounded-lg border border-slate-200 bg-white p-5">
              <h3 className="text-sm font-semibold text-atlas-ink mb-3">5 · Risk factors</h3>
              <div className="grid grid-cols-2 gap-1.5">
                {(meta?.riskFactors || []).map((r) => (
                  <label key={r.key} className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                    <input type="checkbox" checked={riskFlags.includes(r.key)} onChange={() => toggleRisk(r.key)} data-testid={`wf-risk-${r.key}`} />
                    {r.label}
                  </label>
                ))}
              </div>
              <Field label="Notes"><textarea className="rounded-md border border-slate-300 px-3 py-2 text-sm mt-3" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} /></Field>
            </section>

            <div className="flex gap-3 sticky bottom-4">
              <Button disabled={busy} onClick={() => run(false)} className="flex-1 bg-atlas-ink hover:bg-atlas-blue text-white h-11" data-testid="wf-calculate">{busy ? "Calculating…" : "Calculate opportunity"}</Button>
              <Button disabled={busy || !result} onClick={() => run(true)} className="flex-1 bg-atlas-blue hover:bg-atlas-sky text-white h-11" data-testid="wf-save">Save opportunity</Button>
            </div>
          </div>

          {/* RESULT */}
          <div className="lg:sticky lg:top-24 self-start">
            {result ? <OpportunityIntelligence data={result} /> : (
              <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-10 text-center text-slate-400" data-testid="workspace-empty">
                Fill in the vehicle, corridor, price and comparables, then <span className="text-atlas-blue font-medium">Calculate opportunity</span> to see the Atlas decision, maximum purchase price and margin.
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
